import { Flex, Notice, SidebarContent, SidebarPart } from '@eleveo/components-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useIntegrations } from '../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../constants/dataTestIds';
import { getSupportedJobs, getSupportedStreams } from '../../../scene/integrations/config';
import {
  WebexCCConfigurationType
} from '../../../scene/integrations/types';
import { getWebexCCRootState } from '../../../utils/formUtils';
import { ETL_INTEGRATION_TYPE } from '../../configuration/types';
import { IntegrationTitle } from '../../sideBar/integrations/IntegrationTitle';
import { IntegrationsHeader } from '../../sideBar/integrations/IntegrationsHeader';
import { FieldAccessToken } from '../fields/FieldAccessToken';
import { FieldApiUrl } from '../fields/FieldApiUrl';
import { FieldClientId } from '../fields/FieldClientId';
import { FieldClientSecret } from '../fields/FieldClientSecret';
import { FieldIntegrationName } from '../fields/FieldIntegrationName';
import { FieldOrganizationId } from '../fields/FieldOrganizationId';
import { FieldRefreshToken } from '../fields/FieldRefreshToken';
import { IntegrationJobs } from '../jobs/IntegrationJobs';
import { RoleMapping } from '../jobs/userSync/RoleMapping';
import { IntegrationRolesProvider } from '../roles/IntegrationRolesProvider';

export const WebexCCForm: React.FC = () => {
  const { t } = useTranslation();

  const { integrationType } = useIntegrations();

  if (integrationType !== ETL_INTEGRATION_TYPE.wxcc) throw new Error('WebexCCForm: invalid integrationType!');

  const [isWarningActive, setIsWarningActive] = useState(false);

  const { values: formState } = useFormState<WebexCCConfigurationType>({ subscription: { values: true } });

  const { wxccExpiresIn, wxccClientId, wxccClientSecret } =
    getWebexCCRootState(formState);

  useEffect(() => {
    if (wxccExpiresIn === undefined) {
      // no authorization, don't show warning
      return;
    }
    setIsWarningActive(true);
  }, [wxccClientId, wxccClientSecret, wxccExpiresIn]);

  const availableJobs = useMemo(() => getSupportedJobs(integrationType), [integrationType]);
  const availableStreams = useMemo(() => getSupportedStreams(integrationType), [integrationType]);

  return (
    <IntegrationRolesProvider>
      <IntegrationsHeader />
      <SidebarContent>
        <SidebarPart>
          <Flex column gap={20} className="p-20">
            <IntegrationTitle />
            <FieldIntegrationName />
            <FieldApiUrl />
            <FieldOrganizationId />
            <FieldClientId />
            <FieldClientSecret />
            <FieldAccessToken />
            <FieldRefreshToken />

            {isWarningActive && (
              <Notice color="warning" testId={TEST_IDS.noticeConfigurationWarning}>
                {t('myConfigurations.warning')}
              </Notice>
            )}
            <IntegrationJobs availableJobs={availableJobs} availableStreams={availableStreams} />
          </Flex>
        </SidebarPart>
        <SidebarPart>
          <RoleMapping />
        </SidebarPart>
      </SidebarContent>
    </IntegrationRolesProvider>
  );
};
